import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Platform, StyleSheet, View} from 'react-native';
import {BottomTabNavigationOptions} from '@react-navigation/bottom-tabs';
import {observer} from 'mobx-react-lite';

import {CatalogueErrorFallback} from '../components/CatalogueErrorFallback';
import LoadingImg from '../components/LoadingImg';
import ScrollToTopButton from '../components/ScrollToTopButton';
import {fontFamily} from '../components/ui-controls/Text';
import {ScrollCollapseProvider, TAB_BAR_HEIGHT} from '../context/ScrollCollapseContext';
import {SearchProvider} from '../context/SearchContext';
import {useEdgeToEdgeBottomInset} from '../hooks/useEdgeToEdgeBottomInset';
import {useStoreSync} from '../hooks/useStoreSync';
import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import {AppRoute} from '../models/AppRoute';
import type {AppRoutesNavigatorScreenProps} from '../models/navigationTypes';
import CatalogueScreen from '../screens/catalogue/CatalogueScreen';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import {logger} from '../utils/logger';

import {AnimatedTabBar} from './components/AnimatedTabBar';
import useCommonScreenOptions from './hooks/useCommonScreenOptions';
import {getRoutesForStoreType} from './routes/routes';
import {StoreType} from './routes/types';
import {Stack, Tab} from './navigators';
import {shouldDetachInactiveScreens} from './util';

const EmptyComponent = () => <View />;

// BottomTabNavigator on Mobile, Stack Navigator on Web Desktop
const AppRoutesNavigator = observer((_: AppRoutesNavigatorScreenProps) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const bottomInset = useEdgeToEdgeBottomInset();
  const {shopSelectionStore, userStore} = useStores();
  const {isLoggedIn} = userStore;

  const TabComponent = isWebDesktopScreen ? Stack : Tab;

  const commonScreenOptions = useCommonScreenOptions(true);
  const tabBarStyle = {
    ...styles.tabBarStyle,
    height: TAB_BAR_HEIGHT + bottomInset,
    paddingBottom: bottomInset,
  };
  const bottomTabOptions: BottomTabNavigationOptions = {
    tabBarIconStyle: styles.icon,
    tabBarItemStyle: styles.item,
    tabBarStyle,
    tabBarShowLabel: true,
    tabBarActiveTintColor: theme.colour.navTab.activeText,
    tabBarInactiveTintColor: theme.colour.navTab.inactiveText,
    tabBarLabelStyle: {
      fontFamily,
      fontWeight: '600',
      fontSize: 10,
      letterSpacing: 0.5,
    },
  };
  const screenOptions = {
    ...commonScreenOptions,
    ...(isWebDesktopScreen ? {} : bottomTabOptions),
  };

  const storeType = shopSelectionStore?.selectedStoreType ?? StoreType.LiquorStore;

  // Defensive: Handle race condition where routes may be undefined during initialization
  const routes =
    getRoutesForStoreType(
      storeType,
      shopSelectionStore.loyaltyEnabled,
      shopSelectionStore.ecommerceDisabled,
      isLoggedIn,
    ) || [];

  const navigatorContent = (
    <TabComponent.Navigator
      backBehavior="history"
      // DIS-1420: native tab-Screen detach/reattach during the post-order nav
      // storm trips rn-screens "addViewAt: already has a parent". Keep web; not on native.
      detachInactiveScreens={shouldDetachInactiveScreens()}
      // @ts-expect-error - screenOptions type mismatch between Stack and Tab navigators
      screenOptions={screenOptions}
      initialRouteName={shopSelectionStore?.homeAppRoute}
      // We may need to bottom tabs in the Gift Card Store. Just hiding them for now.
      // Web has no bottom tab bar (QA 2026-10-21): the header and footer carry its links.
      // Native keeps it. Web desktop renders a Stack, which ignores tabBar.
      tabBar={
        storeType === StoreType.GiftCardStore || Platform.OS === 'web'
          ? EmptyComponent
          : props => <AnimatedTabBar {...props} />
      }>
      {routes.map(
        ({
          name,
          component,
          icon: tabBarIcon,
          tabBarLabel,
          showInTab,
          header,
          headerTransparent,
          initialParams,
          listeners,
        }) => {
          // Special handling for CATALOGUE route: wrap with Suspense + ErrorBoundary
          const isCatalogueRoute = name === AppRoute.CATALOGUE;
          const ScreenComponent = isCatalogueRoute
            ? () => (
                <ErrorBoundary
                  FallbackComponent={CatalogueErrorFallback}
                  onError={(error, errorInfo) => {
                    const is404 = error.message?.includes('404');
                    const logFn = is404 ? logger.warn : logger.error;

                    logFn('CatalogueErrorBoundary', 'Caught error', {
                      error: error.message,
                      stack: error.stack,
                      componentStack: errorInfo.componentStack,
                    });
                  }}>
                  <Suspense fallback={<LoadingImg />}>
                    <CatalogueScreen />
                  </Suspense>
                </ErrorBoundary>
              )
            : component;

          return (
            <TabComponent.Screen
              key={name} // react key
              name={name}
              component={ScreenComponent}
              getId={() => name}
              initialParams={initialParams}
              // @ts-expect-error - listeners type mismatch between Stack and Tab navigators
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              listeners={({navigation, route}: {navigation: any; route: any}) => {
                const resolvedListeners =
                  typeof listeners === 'function'
                    ? listeners({navigation, route})
                    : listeners || {};

                if (isWebDesktopScreen) {
                  return {
                    ...resolvedListeners,
                    // On desktop (Stack), intercept tab presses to behave like tabs (popTo if exists)
                    // Stack Navigator doesn't have tabPress, but if we ever add it or it bubbles
                    // prevent default to handle manually.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    tabPress: (e: any) => {
                      e.preventDefault();
                      // @ts-expect-error - tabPress might not exist on resolvedListeners
                      resolvedListeners.tabPress?.(e);
                    },
                  };
                }

                return resolvedListeners;
              }}
              // @ts-expect-error - options type mismatch between Stack and Tab navigators
              options={{
                header,
                // DIS-1127: absolute-position the header when the route opts in,
                // so scroll-collapse screens don't trigger a Yoga layout feedback
                // loop on Android. Route specs set headerTransparent=true and the
                // consuming screen must add paddingTop: useHeaderHeight() on its
                // scroll content container.
                ...(headerTransparent !== undefined ? {headerTransparent} : {}),
                // Only apply Bottom Tab specific options when NOT using Stack navigator
                ...(isWebDesktopScreen
                  ? {} // Stack navigator - no tab bar options (popTo() handles navigation)
                  : {
                      // Bottom Tab navigator - apply tab bar options
                      tabBarActiveBackgroundColor: theme.colour.navTab.activeBg,
                      tabBarIcon,
                      // Stable e2e handle per tab. The tab label is a plain TextView
                      // Espresso refuses to tap, and a testID on the icon SVG does not
                      // reach the native tree. React Navigation puts this one on the
                      // pressable itself. See DIS-1653.
                      tabBarButtonTestID: `tab-${name}`,
                      ...(tabBarLabel !== undefined ? {tabBarLabel} : {}),
                      tabBarButton: showInTab ? undefined : () => null,
                      // Completely hide tab items from layout on web when showInTab is false
                      // Using display: 'none' prevents empty containers from being rendered
                      tabBarItemStyle: showInTab ? undefined : {display: 'none'},
                    }),
              }}
            />
          );
        },
      )}
    </TabComponent.Navigator>
  );

  // Provider wraps both desktop and mobile — shared values are inert on desktop
  // but screens always call useScrollCollapseHandler() regardless of platform
  return (
    <ScrollCollapseProvider>
      <SearchProvider>
        <StoreSyncBridge />
        {navigatorContent}
        <ScrollToTopButton />
      </SearchProvider>
    </ScrollCollapseProvider>
  );
});

function StoreSyncBridge() {
  useStoreSync();
  return null;
}

const styles = StyleSheet.create({
  tabBarStyle: {
    borderTopWidth: 0.5,
    borderTopColor: 'grey',
    backgroundColor: theme.colour.navTab.barBg,
  },
  icon: {
    flex: 1,
    width: '100%',
  },
  item: {
    justifyContent: 'center',
    width: '100%',
  },
});

export default AppRoutesNavigator;
